import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { decodePostScoreQR } from '@scoring/shared';
import type { PostScoreQRPayload, PostScoreQRParticipant } from '@scoring/shared';
import {
  AppLayout,
  ConnectivityIndicator,
  Button,
  ErrorBanner,
} from '@scoring/frontend-shared';
import type { ParticipantScoreView, ParticipantStationScore } from '@scoring/frontend-shared';

const SCORES_CACHE_KEY = 'participant_scores_cache';

/**
 * Scan page — scans a post-score QR code generated by the score taker,
 * displays the decoded scores, and merges them into the local scores cache.
 *
 * Requirements:
 *   - 20.4: participant scans post-score QR to view scores
 *   - 20.5: update local cache with scores from QR
 *   - 20.7: server data supersedes locally cached QR data on reconnect
 */
export function ScanPage(): JSX.Element {
  const navigate = useNavigate();
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerRef = useRef<HTMLDivElement | null>(null);

  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scannedPayload, setScannedPayload] = useState<PostScoreQRPayload | null>(null);
  const [merged, setMerged] = useState(false);

  const startScanner = useCallback(async () => {
    setError(null);
    setScannedPayload(null);
    setMerged(false);

    if (!scannerContainerRef.current) return;

    try {
      const scanner = new Html5Qrcode('qr-reader');
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          void handleScan(decodedText, scanner);
        },
        () => {
          // QR scan failure (no code found in frame) — ignore
        },
      );

      setScanning(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? `Camera error: ${err.message}`
          : 'Unable to access camera. Please grant camera permissions.',
      );
    }
  }, []);

  const handleScan = async (decodedText: string, scanner: Html5Qrcode): Promise<void> => {
    // Stop scanning immediately on successful read
    try {
      await scanner.stop();
    } catch {
      // already stopped
    }
    scannerRef.current = null;
    setScanning(false);

    const result = decodePostScoreQR(decodedText);

    if (!result.ok) {
      setError(`Invalid QR code: ${result.error}`);
      return;
    }

    const payload = result.value;
    setScannedPayload(payload);

    // Merge into local cache (Req 20.5)
    mergeIntoCache(payload);
    setMerged(true);
  };

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      const scanner = scannerRef.current;
      if (scanner) {
        void scanner.stop().catch(() => {});
        scannerRef.current = null;
      }
    };
  }, []);

  // Auto-start scanner on mount
  useEffect(() => {
    void startScanner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScanAnother = useCallback(() => {
    setScannedPayload(null);
    setMerged(false);
    setError(null);
    void startScanner();
  }, [startScanner]);

  const handleGoToScores = useCallback(() => {
    navigate('/scores');
  }, [navigate]);

  return (
    <AppLayout
      title="Scan QR Code"
      headerRight={<ConnectivityIndicator />}
    >
      {error && (
        <ErrorBanner title="Scan Error" onDismiss={() => setError(null)}>
          {error}
        </ErrorBanner>
      )}

      {/* Scanner viewport */}
      {!scannedPayload && (
        <section aria-label="QR code scanner">
          <p style={{ color: 'var(--color-text-secondary, #6B7280)', marginBottom: '1rem', fontSize: 'var(--font-size-body-sm, 0.875rem)' }}>
            Point your camera at the post-score QR code shown by the score taker.
          </p>
          <div
            id="qr-reader"
            ref={scannerContainerRef}
            style={{
              width: '100%',
              maxWidth: '400px',
              margin: '0 auto',
              borderRadius: 'var(--radius-card, 12px)',
              overflow: 'hidden',
            }}
          />
          {!scanning && !error && (
            <div style={{ textAlign: 'center', marginTop: '1rem' }}>
              <Button onClick={() => void startScanner()}>Start Scanner</Button>
            </div>
          )}
        </section>
      )}

      {/* Scan result */}
      {scannedPayload && (
        <section aria-label="Scan result">
          <ConfirmationBanner
            stationNumber={scannedPayload.s}
            participants={scannedPayload.p}
          />

          {/* Score details table */}
          <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: 'var(--font-size-body-sm, 0.875rem)',
              }}
              aria-label="Scanned scores"
            >
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-border, #E5E7EB)', textAlign: 'left' }}>
                  <th style={thStyle}>Participant</th>
                  <th style={thStyle}>Hits</th>
                  <th style={thStyle}>Misses</th>
                  <th style={thStyle}>DNS</th>
                </tr>
              </thead>
              <tbody>
                {scannedPayload.p.map((p) => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #eaeaea' }}>
                    <td style={tdStyle}>{p.id}</td>
                    <td style={tdStyle}>{p.h}</td>
                    <td style={tdStyle}>{p.m}</td>
                    <td style={tdStyle}>
                      {p.dns ? (
                        <span style={dnsBadgeStyle}>DNS</span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {merged && (
            <p
              style={{
                marginTop: '1rem',
                padding: '0.5rem 0.75rem',
                backgroundColor: '#e6f4ea',
                borderRadius: 'var(--radius-button, 8px)',
                color: 'var(--color-brand-green, #1B4332)',
                fontSize: 'var(--font-size-body-sm, 0.875rem)',
              }}
              role="status"
            >
              ✓ Scores saved to local cache
            </p>
          )}

          {/* Actions */}
          <div
            style={{
              display: 'flex',
              gap: '0.75rem',
              marginTop: '1.5rem',
              flexWrap: 'wrap',
            }}
          >
            <Button variant="secondary" onClick={handleScanAnother}>
              Scan Another
            </Button>
            <Button onClick={handleGoToScores}>
              My Scores
            </Button>
          </div>
        </section>
      )}
    </AppLayout>
  );
}

// --- Helper: merge scanned scores into localStorage cache ---

function mergeIntoCache(payload: PostScoreQRPayload): void {
  try {
    const raw = localStorage.getItem(SCORES_CACHE_KEY);
    if (!raw) {
      // No existing cache — create a minimal one from the QR data
      const newCache: ParticipantScoreView = buildScoreViewFromPayload(payload);
      localStorage.setItem(SCORES_CACHE_KEY, JSON.stringify(newCache));
      return;
    }

    const cached = JSON.parse(raw) as ParticipantScoreView;

    // Merge each participant's score for this station into the cached view.
    // The cache stores the current user's scores, so we merge any matching
    // participant entries. Since we don't know which participant the current
    // user is from the QR alone, we merge all entries — the MyScoresPage
    // will display only the user's own data when it re-fetches from server.
    for (const p of payload.p) {
      const newStation: ParticipantStationScore = {
        stationNumber: payload.s,
        hits: p.h,
        misses: p.m,
        isDns: p.dns,
        runningTotal: 0, // will be recalculated below
      };

      // Check if this station already exists in the cached stations
      const existingIdx = cached.stations.findIndex(
        (s) => s.stationNumber === payload.s,
      );

      if (existingIdx >= 0) {
        // Update existing station score
        cached.stations[existingIdx] = newStation;
      } else {
        // Add new station score
        cached.stations.push(newStation);
      }

      // Remove from remaining stations if present
      cached.remainingStations = cached.remainingStations.filter(
        (n) => n !== payload.s,
      );
    }

    // Sort stations by number
    cached.stations.sort((a, b) => a.stationNumber - b.stationNumber);

    // Recalculate running totals
    let runningTotal = 0;
    for (const station of cached.stations) {
      runningTotal += station.hits;
      station.runningTotal = runningTotal;
    }

    // Update summary
    cached.totalScore = runningTotal;
    cached.stationsCompleted = cached.stations.length;

    localStorage.setItem(SCORES_CACHE_KEY, JSON.stringify(cached));
  } catch {
    // localStorage unavailable or corrupt — non-critical
  }
}

function buildScoreViewFromPayload(payload: PostScoreQRPayload): ParticipantScoreView {
  const stations: ParticipantStationScore[] = payload.p.map((p) => ({
    stationNumber: payload.s,
    hits: p.h,
    misses: p.m,
    isDns: p.dns,
    runningTotal: p.h,
  }));

  // Use only the first participant entry for the view (best effort)
  const first = stations[0];
  return {
    participantId: '',
    participantName: '',
    teamName: null,
    totalScore: first?.hits ?? 0,
    stationsCompleted: 1,
    stations: first ? [first] : [],
    remainingStations: [],
  };
}

// --- Sub-components ---

interface ConfirmationBannerProps {
  stationNumber: number;
  participants: PostScoreQRParticipant[];
}

function ConfirmationBanner({ stationNumber, participants }: ConfirmationBannerProps): JSX.Element {
  const totalHits = participants.reduce((sum, p) => sum + p.h, 0);

  return (
    <div
      style={{
        padding: '1rem',
        backgroundColor: '#e8f5e9',
        borderRadius: 'var(--radius-card, 12px)',
        border: '1px solid #a5d6a7',
      }}
      role="alert"
      aria-live="polite"
    >
      <h2 style={{ margin: '0 0 0.25rem', fontSize: 'var(--font-size-heading-sm, 1.25rem)', color: 'var(--color-brand-green, #1B4332)' }}>
        Score received: Station {stationNumber} — {totalHits} hits
      </h2>
      <p style={{ margin: 0, fontSize: 'var(--font-size-body-sm, 0.875rem)', color: 'var(--color-text-secondary, #6B7280)' }}>
        {participants.length} participant{participants.length !== 1 ? 's' : ''} scored
      </p>
    </div>
  );
}

// --- Styles ---

const thStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  fontWeight: 600,
  fontSize: 'var(--font-size-body-sm, 0.875rem)',
  color: 'var(--color-text-secondary, #6B7280)',
};

const tdStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
};

const dnsBadgeStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '0.125rem 0.4rem',
  borderRadius: 'var(--radius-button, 8px)',
  fontSize: 'var(--font-size-body-sm, 0.875rem)',
  fontWeight: 600,
  backgroundColor: '#fff3cd',
  color: '#856404',
};
